import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { bookmarksAPI, tendersAPI } from '../services/api';
import { logEvent } from '../utils/eventLog';

const BookmarkContext = createContext();

const BOOKMARKS_STORAGE_KEY = 'canadabuys-bookmarks';

// Normalize backend snake_case → frontend camelCase
const normalizeBookmark = (b) => ({
  ...b,
  id: b.tender_id || b.id,
  tenderId: b.tender_id || b.id,
  tender_id: b.tender_id || b.id,
  pipelineStage: b.pipeline_stage || b.pipelineStage || 'reviewing',
  analysisStatus: b.analysis_status || b.analysisStatus || null,
  analyzedAt: b.analyzed_at || b.analyzedAt || null,
  bidRecommendation: b.bid_recommendation || b.bidRecommendation || null,
  closingDate: b.closing_date || b.closingDate || null,
  closing_date: b.closing_date || b.closingDate || null,
  bookmarkedAt: b.bookmarked_at || b.bookmarkedAt || null,
});

export const useBookmarks = () => {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error('useBookmarks must be used within a BookmarkProvider');
  }
  return context;
};

export const BookmarkProvider = ({ children }) => {
  const [bookmarks, setBookmarks] = useState([]);
  // Mirror of `bookmarks` for synchronous ownership checks without adding `bookmarks`
  // to callback deps (which would re-trigger the analysis-sync PATCH storm).
  const bookmarksRef = useRef([]);
  useEffect(() => { bookmarksRef.current = bookmarks; }, [bookmarks]);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle | syncing | synced | error
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [syncError, setSyncError] = useState(null);
  // Team-wide saved count (unified view) for the nav badges, so they match the
  // team list on My Tenders rather than showing only the caller's saves.
  const [teamCount, setTeamCount] = useState(null);
  useEffect(() => {
    bookmarksAPI.getBookmarks('team')
      .then((d) => setTeamCount(Array.isArray(d) ? d.length : null))
      .catch(() => {});
  }, []);

  // Check if user is authenticated (dev mode bypasses authToken requirement)
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const devMode = process.env.REACT_APP_DEV_MODE === 'true';
    const authenticated = !!token || devMode;
    setIsAuthenticated(authenticated);
    return authenticated;
  }, []);

  // Load bookmarks from localStorage
  const loadLocalBookmarks = useCallback(() => {
    try {
      const saved = localStorage.getItem(BOOKMARKS_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved).map(normalizeBookmark);
      }
    } catch (error) {
      console.error('Error loading local bookmarks:', error);
    }
    return [];
  }, []);

  // Save bookmarks to localStorage (cache)
  const saveLocalBookmarks = useCallback((bookmarksToSave) => {
    try {
      localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(bookmarksToSave));
    } catch (error) {
      console.error('Error saving local bookmarks:', error);
    }
  }, []);

  // Closing dates / status are now refreshed server-side: the team feed
  // (get_all_bookmarks) and the per-user list are enriched on the backend in a
  // single query. The old client fan-out (a getTender per bookmark, up to 50
  // concurrent) saturated the single tenders worker and starved the team-feed
  // call (10s timeouts) — so this is a no-op now.
  const enrichBookmarksWithLiveData = useCallback(async (bookmarksToEnrich) => {
    return bookmarksToEnrich;
  }, []);

  // Reconcile local bookmarks with backend (replaces one-time sync)
  const reconcileWithBackend = useCallback(async (localBookmarks) => {
    if (!checkAuth()) return null;

    try {
      setSyncStatus('syncing');
      setSyncError(null);

      const result = await bookmarksAPI.reconcile(localBookmarks);
      const normalized = (result.bookmarks || []).map(normalizeBookmark);

      setSyncStatus('synced');
      console.log(`Reconciled: ${result.created} created, ${result.updated} updated, ${result.total} total`);
      return normalized;
    } catch (error) {
      if (error.response?.status === 401) {
        setSyncStatus('idle');
        return null;
      }
      console.error('Error reconciling with backend:', error);
      setSyncStatus('error');
      setSyncError('Failed to sync bookmarks to cloud');
      return null;
    }
  }, [checkAuth]);

  // Load bookmarks from backend
  const loadFromBackend = useCallback(async () => {
    if (!checkAuth()) return null;

    try {
      const backendBookmarks = await bookmarksAPI.getBookmarks();
      return backendBookmarks.map(normalizeBookmark);
    } catch (error) {
      if (error.response?.status === 401) {
        return null;
      }
      console.error('Error loading from backend:', error);
      setSyncError('Failed to load bookmarks from cloud');
      return null;
    }
  }, [checkAuth]);

  // Initialize bookmarks on mount — ES-first for authenticated users
  useEffect(() => {
    const initializeBookmarks = async () => {
      setLoading(true);

      const hasAuth = checkAuth();
      const localBookmarks = loadLocalBookmarks();

      if (hasAuth) {
        // Show local bookmarks immediately (optimistic)
        if (localBookmarks.length > 0) {
          setBookmarks(localBookmarks);
        }

        // Reconcile with backend in background
        const canonical = await reconcileWithBackend(localBookmarks);
        if (canonical) {
          setBookmarks(canonical);
          saveLocalBookmarks(canonical);
        } else {
          // Reconcile failed — try plain load
          const backendBookmarks = await loadFromBackend();
          if (backendBookmarks) {
            setBookmarks(backendBookmarks);
            saveLocalBookmarks(backendBookmarks);
          }
          // else keep local
        }
      } else {
        // Not authenticated — localStorage only
        setBookmarks(localBookmarks);
      }

      setLoading(false);

      // Enrich closing dates from live tender data (works for both auth and local-only)
      // Runs after initial load so UI appears fast, then updates stale dates
      enrichBookmarksWithLiveData(localBookmarks).then(enriched => {
        if (enriched !== localBookmarks) {
          setBookmarks(enriched);
        }
      });
    };

    initializeBookmarks();
  }, [checkAuth, loadLocalBookmarks, loadFromBackend, reconcileWithBackend, saveLocalBookmarks, enrichBookmarksWithLiveData]);

  // Re-check auth when localStorage changes (e.g., login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        checkAuth();
        if (e.newValue) {
          // Just logged in — reconcile
          const local = loadLocalBookmarks();
          reconcileWithBackend(local).then(canonical => {
            if (canonical) {
              setBookmarks(canonical);
              saveLocalBookmarks(canonical);
            }
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth, loadLocalBookmarks, reconcileWithBackend, saveLocalBookmarks]);

  const addBookmark = useCallback(async (tender) => {
    const tenderId = tender.tender_id || tender.tenderId || tender.id || tender.reference_number;
    const bookmark = {
      id: tenderId,
      tenderId: tenderId,
      tender_id: tenderId,
      reference_number: tender.reference_number || tenderId,
      title: tender.title || 'Untitled Tender',
      organization: tender.organization?.contracting_entity ||
                   tender.organization?.name ||
                   tender.organization ||
                   'Government of Canada',
      status: tender.status || 'Open',
      publishDate: tender.publish_date,
      closingDate: tender.closing_date,
      closing_date: tender.closing_date,
      bookmarkedAt: new Date().toISOString(),
      categories: tender.categories || [],
      location: tender.location || {},
      pipelineStage: 'reviewing',
      analysisStatus: null,
      analyzedAt: null,
      bidRecommendation: null,
      ...tender
    };

    // Optimistically update local state
    setBookmarks(prev => {
      if (prev.some(b => b.id === bookmark.id || b.tender_id === bookmark.tender_id)) {
        return prev;
      }
      const updated = [bookmark, ...prev];
      saveLocalBookmarks(updated);
      return updated;
    });

    // Sync to backend if authenticated
    if (checkAuth()) {
      try {
        await bookmarksAPI.addBookmark(bookmark);
      } catch (error) {
        console.error('Error adding bookmark to backend:', error);
      }
    }
    logEvent('bookmark.created', {
      tender_id: bookmark.tender_id,
      meta: { title: bookmark.title, organization: bookmark.organization },
    });
  }, [checkAuth, saveLocalBookmarks]);

  const removeBookmark = useCallback(async (tenderId) => {
    // Optimistically update local state
    setBookmarks(prev => {
      const updated = prev.filter(bookmark =>
        bookmark.id !== tenderId &&
        bookmark.tender_id !== tenderId
      );
      saveLocalBookmarks(updated);
      return updated;
    });

    // Sync to backend if authenticated
    if (checkAuth()) {
      try {
        await bookmarksAPI.removeBookmark(tenderId);
      } catch (error) {
        console.error('Error removing bookmark from backend:', error);
      }
    }
  }, [checkAuth, saveLocalBookmarks]);

  const isBookmarked = useCallback((tenderId) => {
    return bookmarks.some(bookmark =>
      bookmark.id === tenderId ||
      bookmark.tender_id === tenderId
    );
  }, [bookmarks]);

  const toggleBookmark = useCallback((tender) => {
    const tenderId = tender.tender_id || tender.id || tender.reference_number;
    if (isBookmarked(tenderId)) {
      removeBookmark(tenderId);
    } else {
      addBookmark(tender);
    }
  }, [isBookmarked, addBookmark, removeBookmark]);

  const clearAllBookmarks = useCallback(async () => {
    const currentBookmarks = bookmarks;
    setBookmarks([]);
    saveLocalBookmarks([]);

    if (checkAuth()) {
      try {
        for (const bookmark of currentBookmarks) {
          await bookmarksAPI.removeBookmark(bookmark.tender_id || bookmark.id);
        }
      } catch (error) {
        console.error('Error clearing bookmarks from backend:', error);
      }
    }
  }, [bookmarks, checkAuth, saveLocalBookmarks]);

  const searchBookmarks = useCallback((query) => {
    if (!query.trim()) return bookmarks;

    const searchTerm = query.toLowerCase();
    return bookmarks.filter(bookmark =>
      (bookmark.title || '').toLowerCase().includes(searchTerm) ||
      (bookmark.organization || '').toLowerCase().includes(searchTerm) ||
      (bookmark.categories || []).some(cat => cat.toLowerCase().includes(searchTerm))
    );
  }, [bookmarks]);

  // Export bookmarks to JSON file
  const exportBookmarks = useCallback(async () => {
    try {
      if (checkAuth()) {
        const blob = await bookmarksAPI.exportBookmarks();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `aiquire-bookmarks-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const exportData = {
          version: '1.0',
          exported_at: new Date().toISOString(),
          source: window.location.hostname,
          bookmark_count: bookmarks.length,
          bookmarks: bookmarks.map(b => ({
            tender_id: b.tender_id || b.id,
            title: b.title,
            organization: b.organization,
            status: b.status,
            closing_date: b.closing_date || b.closingDate,
            notes: b.notes,
            pipeline_stage: b.pipelineStage,
            analysis_status: b.analysisStatus,
            analyzed_at: b.analyzedAt,
            bid_recommendation: b.bidRecommendation,
            bookmarked_at: b.bookmarkedAt || b.bookmarked_at
          }))
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `aiquire-bookmarks-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      return { success: true };
    } catch (error) {
      console.error('Error exporting bookmarks:', error);
      return { success: false, error: error.message };
    }
  }, [bookmarks, checkAuth]);

  // Import bookmarks from JSON file
  const importBookmarks = useCallback(async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.bookmarks || !Array.isArray(data.bookmarks)) {
        throw new Error('Invalid bookmark file format');
      }

      if (checkAuth()) {
        const result = await bookmarksAPI.importBookmarks(data.bookmarks);
        const backendBookmarks = await loadFromBackend();
        if (backendBookmarks) {
          setBookmarks(backendBookmarks);
          saveLocalBookmarks(backendBookmarks);
        }

        return {
          success: true,
          imported: result.imported,
          skipped: result.skipped
        };
      } else {
        let imported = 0;
        let skipped = 0;

        for (const bookmark of data.bookmarks) {
          const tenderId = bookmark.tender_id || bookmark.id;
          if (!tenderId) {
            skipped++;
            continue;
          }

          if (isBookmarked(tenderId)) {
            skipped++;
            continue;
          }

          setBookmarks(prev => {
            const newBookmark = normalizeBookmark({
              tender_id: tenderId,
              title: bookmark.title,
              organization: bookmark.organization,
              status: bookmark.status,
              closing_date: bookmark.closing_date,
              notes: bookmark.notes,
              pipeline_stage: bookmark.pipeline_stage,
              analysis_status: bookmark.analysis_status,
              analyzed_at: bookmark.analyzed_at,
              bid_recommendation: bookmark.bid_recommendation,
              bookmarked_at: bookmark.bookmarked_at || new Date().toISOString()
            });
            const updated = [newBookmark, ...prev];
            saveLocalBookmarks(updated);
            return updated;
          });
          imported++;
        }

        return { success: true, imported, skipped };
      }
    } catch (error) {
      console.error('Error importing bookmarks:', error);
      return { success: false, error: error.message };
    }
  }, [checkAuth, isBookmarked, loadFromBackend, saveLocalBookmarks]);

  // Update arbitrary fields on an existing bookmark — persists to ES
  const updateBookmarkFields = useCallback(async (tenderId, fields) => {
    // Update local state optimistically
    setBookmarks(prev => {
      const updated = prev.map(b => {
        if (b.id === tenderId || b.tender_id === tenderId) {
          return { ...b, ...fields };
        }
        return b;
      });
      saveLocalBookmarks(updated);
      return updated;
    });

    // Persist to backend via PATCH — but ONLY for a bookmark the current user actually
    // owns. In the unified team-wide view we display/sync teammates' saves too, and the
    // per-user PATCH endpoint 404s on those (you have no bookmark doc of your own for
    // that tender). Skipping avoids the wasted request + the benign 404 console noise.
    const owned = bookmarksRef.current.some(
      (b) => b.id === tenderId || b.tender_id === tenderId);
    if (owned && checkAuth()) {
      try {
        await bookmarksAPI.updateBookmark(tenderId, {
          pipeline_stage: fields.pipelineStage || fields.pipeline_stage,
          analysis_status: fields.analysisStatus || fields.analysis_status,
          analyzed_at: fields.analyzedAt || fields.analyzed_at,
          bid_recommendation: fields.bidRecommendation || fields.bid_recommendation,
          notes: fields.notes,
        });
      } catch (error) {
        // A 404 here is the same benign "not your bookmark" case — don't spam the console.
        if (error?.response?.status !== 404) {
          console.error('Error updating bookmark fields on backend:', error);
        }
      }
    }
  }, [checkAuth, saveLocalBookmarks]);

  // Force refresh from backend
  const refreshFromBackend = useCallback(async () => {
    if (!checkAuth()) return false;

    try {
      setSyncStatus('syncing');
      const backendBookmarks = await loadFromBackend();
      if (backendBookmarks) {
        setBookmarks(backendBookmarks);
        saveLocalBookmarks(backendBookmarks);
        setSyncStatus('synced');
        return true;
      }
      setSyncStatus('error');
      return false;
    } catch {
      setSyncStatus('error');
      return false;
    }
  }, [checkAuth, loadFromBackend, saveLocalBookmarks]);

  const value = {
    bookmarks,
    loading,
    syncing: syncStatus === 'syncing',
    syncStatus,
    isAuthenticated,
    syncError,
    addBookmark,
    removeBookmark,
    isBookmarked,
    toggleBookmark,
    clearAllBookmarks,
    searchBookmarks,
    updateBookmarkFields,
    exportBookmarks,
    importBookmarks,
    refreshFromBackend,
    bookmarkCount: teamCount ?? bookmarks.length
  };

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
};
